import { useCallback, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery, useMutation } from '@apollo/client';
import NavBar from '../../components/NavBar/NavBar';
import { NavBarContext } from '../../App';

import classes from './PayoutDashboard.module.css';
import {
  GET_PAYOUT_INFO,
  GET_USER_STRIPE_STATUS,
  CREATE_ONBOARDING_LINK,
  STRIPE_WEB_RETURN_URL,
  STRIPE_WEB_REFRESH_URL,
} from '../../graphql/queries/financeQueries';
import { trackPayoutSetupStarted } from '../../util/tracking';
import backArrow from '../../public/icons/ZurückIcon.svg';
import { notifyErrorToast } from '../../util/notifyActionError';

const formatCurrency = (amount, currency = 'eur') => {
  if (amount == null) return '0,00 €';
  const euros = amount / 100;
  return euros.toLocaleString('de-DE', {
    style: 'currency',
    currency: currency.toUpperCase(),
  });
};

const sumBalance = (items = []) => {
  if (!items.length) {
    return { amount: 0, currency: 'eur' };
  }
  const currency = items[0]?.currency || 'eur';
  const amount = items.reduce((total, item) => total + (item?.amount || 0), 0);
  return { amount, currency };
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const PayoutDashboard = () => {
  const navigate = useNavigate();
  const { isPhoneSize } = useContext(NavBarContext);
  const { user } = useSelector((state) => state?.auth || {});
  const userId = user?.id;

  const { data: payoutData, loading: payoutLoading } = useQuery(GET_PAYOUT_INFO, {
    fetchPolicy: 'cache-and-network',
  });

  const { data: userData } = useQuery(GET_USER_STRIPE_STATUS, {
    variables: { id: userId },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  const [createOnboardingLink, { loading: linkLoading }] = useMutation(CREATE_ONBOARDING_LINK);

  const payoutInfo = payoutData?.getPayoutInfo;
  const userStripe = userData?.getUser;

  const hasCompleteUserStripeSetup = Boolean(
    userStripe?.stripeOnboardingComplete &&
      userStripe?.stripePayoutsEnabled &&
      userStripe?.stripeChargesEnabled
  );
  const isSetupComplete = payoutInfo?.canReceivePayouts ?? hasCompleteUserStripeSetup;

  const availableSummary = sumBalance(payoutInfo?.available || []);
  const pendingSummary = sumBalance(payoutInfo?.pending || []);
  const availableBalance = availableSummary.amount;
  const pendingBalance = pendingSummary.amount;
  const availableCurrency = availableSummary.currency;
  const pendingCurrency = pendingSummary.currency;
  const queuedPayoutAmount = Number(payoutInfo?.queuedPayoutAmount || 0);
  const queuedPayoutCount = Number(payoutInfo?.queuedPayoutCount || 0);
  const failedPayoutAmount = Number(payoutInfo?.failedPayoutAmount || 0);

  const handleOpenStripeDashboard = useCallback(async () => {
    if (!userStripe?.stripeConnectAccountId) {
      notifyErrorToast('Auszahlungskonto konnte nicht gefunden werden.', { title: 'Auszahlungskonto' });
      return;
    }
    // Open tab synchronously before any await so Safari treats it as a direct user gesture.
    const stripeTab = window.open('about:blank', '_blank');
    if (stripeTab) {
      // Sever the opener reference (replaces the old noopener flag).
      stripeTab.opener = null;
      try {
        stripeTab.document.write('<p style="font-family:sans-serif;padding:24px">Weiterleitung zu Stripe …</p>');
      } catch (_) {}
    }
    try {
      const { data } = await createOnboardingLink({
        variables: {
          accountId: userStripe.stripeConnectAccountId,
          returnUrl: STRIPE_WEB_RETURN_URL,
          refreshUrl: STRIPE_WEB_REFRESH_URL,
        },
      });
      const url = data?.createOnboardingLink?.url;

      if (url) {
        trackPayoutSetupStarted({
          source: 'finance_payout_dashboard',
          hasAccount: true,
        });
        if (stripeTab) {
          stripeTab.location.replace(url);
        } else {
          window.location.assign(url);
        }
      } else {
        if (stripeTab) stripeTab.close();
        notifyErrorToast('Der Link konnte nicht geöffnet werden. Bitte versuche es erneut.', { title: 'Stripe-Dashboard' });
      }
    } catch (error) {
      if (stripeTab) stripeTab.close();
      notifyErrorToast('Der Link konnte nicht geöffnet werden. Bitte versuche es erneut.', { title: 'Stripe-Dashboard' });
    }
  }, [createOnboardingLink, userStripe?.stripeConnectAccountId]);

  const handleSetupPress = useCallback(() => {
    if (isPhoneSize) {
      navigate('/profile/account-details/payments');
    } else {
      navigate('/profile', { state: { activeSection: 'auszahlung' } });
    }
  }, [isPhoneSize, navigate]);

  const handleExportTaxReport = useCallback(async () => {
    try {
      const currentYear = new Date().getFullYear();
      const reportContent = `=== EINNAHMEN-ÜBERSICHT FÜR DAS FINANZAMT ===
Generiert am: ${new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
Zeitraum: 01.01.${currentYear} - 31.12.${currentYear}

KONTOSTATUS
-----------
Verfügbares Guthaben: ${formatCurrency(availableBalance, availableCurrency)}
Bei Stripe ausstehend: ${formatCurrency(pendingBalance, pendingCurrency)}
Noch nicht an Stripe übertragen: ${formatCurrency(queuedPayoutAmount, pendingCurrency)}
Fehlgeschlagene Auszahlungen: ${formatCurrency(failedPayoutAmount, pendingCurrency)}
Zahlungen aktiv: ${payoutInfo?.chargesEnabled ? 'Ja' : 'Nein'}
Auszahlungen aktiv: ${payoutInfo?.payoutsEnabled ? 'Ja' : 'Nein'}

HINWEIS
-------
Für eine vollständige Übersicht aller Transaktionen 
besuchen Sie bitte das Stripe Dashboard unter:
https://dashboard.stripe.com

Diese Übersicht dient nur zur Information und ersetzt 
keine offizielle Steuerbescheinigung.`.trim();

      // Create blob and download
      const blob = new Blob([reportContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Einnahmen-Übersicht-${currentYear}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      notifyErrorToast('Die Übersicht konnte nicht exportiert werden.', { title: 'Export fehlgeschlagen' });
    }
  }, [availableBalance, availableCurrency, pendingBalance, pendingCurrency, queuedPayoutAmount, failedPayoutAmount, payoutInfo]);

  const getStatusConfig = () => {
    if (!isSetupComplete) {
      return { label: 'Inaktiv', bgColor: 'var(--light-grey-2)', textColor: 'var(--dark-grey-3)', icon: '⚪️' };
    }

    if (!payoutInfo) {
      return { label: 'Laden...', bgColor: 'var(--light-grey-2)', textColor: 'var(--dark-grey-3)', icon: '⏳' };
    }

    const status = payoutInfo.accountStatus;

    if (status === 'ENABLED' && payoutInfo.payoutsEnabled && payoutInfo.chargesEnabled) {
      return { label: 'Aktiv', bgColor: 'var(--black)', textColor: 'var(--white)', icon: '✓' };
    }
    if (status === 'RESTRICTED') {
      return { label: 'Eingeschränkt', bgColor: 'var(--dark-grey)', textColor: 'var(--white)', icon: '⚠️' };
    }
    if (status === 'PENDING' || !payoutInfo.detailsSubmitted) {
      return { label: 'Aktion erforderlich', bgColor: 'var(--dark-grey-3)', textColor: 'var(--white)', icon: '!' };
    }
    return { label: 'Aktiv', bgColor: 'var(--black)', textColor: 'var(--white)', icon: '✓' };
  };

  if (payoutLoading && !payoutInfo) {
    return (
      <div className={classes.container}>
        <NavBar fromFinance={true} />
        <div className={classes.navBarPhoneContainer}>
          <header className={classes.header}>
              <div className={classes.backButton} onClick={() => navigate(-1)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="22" viewBox="0 0 13 22" fill="none">
                      <path fillRule="evenodd" clipRule="evenodd" d="M12.4609 20.0297L10.9309 21.457L0.960938 10.5378L10.9309 0.457031L12.4017 1.9442L3.84334 10.5977L12.4609 20.0297Z" fill="#101010"/>
                  </svg>
              </div>
              <Link to='/' className={classes.logoLink}>
                  <div className={classes.logoContainer}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="53" height="19" viewBox="0 0 53 19" fill="none">
                          <path d="M50.5002 9.5C51.8809 9.5 53.0002 8.38071 53.0002 7C53.0002 5.61929 51.8809 4.5 50.5002 4.5C49.1195 4.5 48.0002 5.61929 48.0002 7C48.0002 8.38071 49.1195 9.5 50.5002 9.5Z" fill="#FF003E"/>
                          <path d="M9.93692 9.78445L8.58239 9.67069C5.23081 9.39942 4.10204 9.0494 4.10204 8.2881C4.10204 7.5268 5.52603 7.10677 8.46083 7.10677C11.5606 7.10677 12.8109 7.51805 13.0714 8.62062L13.1148 8.79563H16.5185L16.4838 8.55062C16.1452 5.64542 13.5577 4.23658 8.58239 4.23658C3.60712 4.23658 1.05437 5.61917 1.05437 8.33185C1.05437 10.817 3.09483 12.0158 8.11352 12.4709L9.32912 12.5846C12.6546 12.8734 13.8876 13.3109 13.8876 14.186C13.8876 15.1398 12.3681 15.6298 9.36385 15.6298C5.96017 15.6298 4.2236 14.991 4.05863 13.6697L4.03258 13.4772H0.628906V13.7135C0.785197 16.8899 3.63317 18.5 9.10336 18.5C14.5736 18.5 16.944 16.9949 16.944 14.0285C16.944 11.3858 14.9817 10.1957 9.93692 9.78445Z" fill="#1F1F1F"/>
                          <path d="M42.1415 4.67403V10.3444C42.1415 13.5209 40.0663 15.4897 36.7321 15.4897C33.5976 15.4897 32.3733 14.0809 32.3733 10.4582V4.67403H21.8497V0.5L18.7152 1.83009V13.7834C18.7152 17.0823 20.2954 18.4912 24.003 18.4912C25.2968 18.4912 26.5384 18.2724 27.589 18.0537L27.7627 18.0187V15.2097L27.5022 15.2622C26.139 15.5422 25.5312 15.6473 24.5587 15.6473C22.7614 15.6473 21.8497 15.2185 21.8497 12.8646V7.2992H30.793L29.1433 8.55928V11.482C29.1433 16.2685 31.1577 18.4912 35.4731 18.4912C38.2603 18.4912 40.5525 17.5199 42.1415 15.6648V18.0537H45.3715V4.67403H42.1415Z" fill="#1F1F1F"/>
                      </svg>
                  </div>
              </Link>
          </header>
        </div>
        
        <div className={classes.skeletonContainer}>
          <div className={classes.skeletonBanner} />
          <div className={classes.skeletonCard} />
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig();

  return (
    <div className={classes.container}>

      <NavBar fromFinance={true} />
      <div className={classes.navBarPhoneContainer}>
        <header className={classes.header}>
            <div className={classes.backButton} onClick={() => navigate(-1)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="22" viewBox="0 0 13 22" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12.4609 20.0297L10.9309 21.457L0.960938 10.5378L10.9309 0.457031L12.4017 1.9442L3.84334 10.5977L12.4609 20.0297Z" fill="#101010"/>
                </svg>
            </div>
            <Link to='/' className={classes.logoLink}>
                <div className={classes.logoContainer}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="53" height="19" viewBox="0 0 53 19" fill="none">
                        <path d="M50.5002 9.5C51.8809 9.5 53.0002 8.38071 53.0002 7C53.0002 5.61929 51.8809 4.5 50.5002 4.5C49.1195 4.5 48.0002 5.61929 48.0002 7C48.0002 8.38071 49.1195 9.5 50.5002 9.5Z" fill="#FF003E"/>
                        <path d="M9.93692 9.78445L8.58239 9.67069C5.23081 9.39942 4.10204 9.0494 4.10204 8.2881C4.10204 7.5268 5.52603 7.10677 8.46083 7.10677C11.5606 7.10677 12.8109 7.51805 13.0714 8.62062L13.1148 8.79563H16.5185L16.4838 8.55062C16.1452 5.64542 13.5577 4.23658 8.58239 4.23658C3.60712 4.23658 1.05437 5.61917 1.05437 8.33185C1.05437 10.817 3.09483 12.0158 8.11352 12.4709L9.32912 12.5846C12.6546 12.8734 13.8876 13.3109 13.8876 14.186C13.8876 15.1398 12.3681 15.6298 9.36385 15.6298C5.96017 15.6298 4.2236 14.991 4.05863 13.6697L4.03258 13.4772H0.628906V13.7135C0.785197 16.8899 3.63317 18.5 9.10336 18.5C14.5736 18.5 16.944 16.9949 16.944 14.0285C16.944 11.3858 14.9817 10.1957 9.93692 9.78445Z" fill="#1F1F1F"/>
                        <path d="M42.1415 4.67403V10.3444C42.1415 13.5209 40.0663 15.4897 36.7321 15.4897C33.5976 15.4897 32.3733 14.0809 32.3733 10.4582V4.67403H21.8497V0.5L18.7152 1.83009V13.7834C18.7152 17.0823 20.2954 18.4912 24.003 18.4912C25.2968 18.4912 26.5384 18.2724 27.589 18.0537L27.7627 18.0187V15.2097L27.5022 15.2622C26.139 15.5422 25.5312 15.6473 24.5587 15.6473C22.7614 15.6473 21.8497 15.2185 21.8497 12.8646V7.2992H30.793L29.1433 8.55928V11.482C29.1433 16.2685 31.1577 18.4912 35.4731 18.4912C38.2603 18.4912 40.5525 17.5199 42.1415 15.6648V18.0537H45.3715V4.67403H42.1415Z" fill="#1F1F1F"/>
                    </svg>
                </div>
            </Link>
        </header>
      </div>

      <div className={classes.scrollView}>
        {/* Status Banner */}
        <div className={classes.scrollViewHeader}>
          <button
              type="button"
              className={classes.backButton}
              onClick={() => navigate(-1)}
            >
              <img src={backArrow} alt="Zurück" />
            </button>
          <h1 className={`${classes.scrollViewTitle} titleSemibold3`}>Auszahlungen</h1>
        </div>
        <div className={classes.statusBanner} style={{ backgroundColor: statusConfig.bgColor }}>
          <div className={classes.statusIconContainer}>
            <span className={classes.statusIcon}>{statusConfig.icon}</span>
          </div>
          <div className={classes.statusContent}>
            <div className={classes.statusLabel} style={{ color: statusConfig.textColor }}>
              Kontostatus
            </div>
            <div className={classes.statusValue} style={{ color: statusConfig.textColor }}>
              {statusConfig.label}
            </div>
          </div>
        </div>

        {/* Setup Required - Prominent CTA */}
        {!isSetupComplete && (
          <button
            type="button"
            className={classes.balanceCard}
            onClick={handleSetupPress}
          >
            <div className={classes.setupHeader}>
              <div className={classes.setupIconContainer}>
                <span className={classes.setupIcon}>💳</span>
              </div>
              <div className={classes.setupContent}>
                <div className={classes.setupLabel}>Aktion erforderlich</div>
                <div className={classes.setupTitle}>Auszahlungen einrichten</div>
              </div>
            </div>
            <p className={classes.balanceHint}>
              Tippe hier, um dein Auszahlungskonto zu verbinden.
            </p>
            <div className={classes.setupButton}>
              <span className={classes.setupButtonText}>Jetzt einrichten</span>
            </div>
          </button>
        )}

        {/* Balance Card */}
        {isSetupComplete && (
          <div className={classes.balanceCard}>
            <div className={classes.balanceLabel}>Verfügbares Guthaben</div>
            <div className={classes.balanceAmount}>
              {formatCurrency(availableBalance, availableCurrency)}
            </div>
            <div className={classes.balanceHint}>Kann ausgezahlt werden</div>
          </div>
        )}

        {/* Pending Balance */}
        <div className={classes.pendingBalanceRow}>
          <span className={classes.pendingLabel}>Bei Stripe ausstehend</span>
          <span className={classes.pendingAmount}>
            {formatCurrency(pendingBalance, pendingCurrency)}
          </span>
        </div>

        {queuedPayoutAmount > 0 && (
          <div className={classes.pendingBalanceRow}>
            <span className={classes.pendingLabel}>
              Noch nicht an Stripe übertragen{queuedPayoutCount > 1 ? ` (${queuedPayoutCount})` : ''}
            </span>
            <span className={classes.pendingAmount}>
              {formatCurrency(queuedPayoutAmount, pendingCurrency)}
            </span>
          </div>
        )}

        {failedPayoutAmount > 0 && (
          <div className={classes.pendingBalanceRow}>
            <span className={classes.pendingLabel}>Prüfung erforderlich</span>
            <span className={classes.pendingAmount}>
              {formatCurrency(failedPayoutAmount, pendingCurrency)}
            </span>
          </div>
        )}

        {/* Next Payout */}
        {payoutInfo?.nextPayoutDate && (
          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <span className={classes.cardIcon}>📅</span>
              <span className={classes.cardTitle}>Nächste Auszahlung</span>
            </div>
            <div className={classes.nextPayoutDetails}>
              <span className={classes.nextPayoutDate}>
                {formatDate(payoutInfo.nextPayoutDate)}
              </span>
              {payoutInfo.nextPayoutAmount != null && payoutInfo.nextPayoutAmount > 0 && (
                <span className={classes.nextPayoutAmount}>
                  {formatCurrency(payoutInfo.nextPayoutAmount)}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Fee Transparency Card */}
        <div className={classes.feeCard}>
          <div className={classes.feeHeader}>
            <span className={classes.feeEmoji}>💡</span>
            <span className={classes.feeTitle}>Beispielrechnung</span>
          </div>

          <div className={classes.feeExample}>
            <div className={classes.feeRow}>
              <span className={classes.feeLabel}>Buchung</span>
              <span className={classes.feeValue}>100,00 €</span>
            </div>
            <div className={classes.feeRow}>
              <span className={classes.feeLabelSub}>Plattformgebühr (aktuell 0%)</span>
              <span className={classes.feeValueSub}>-0,00 €</span>
            </div>
            <div className={classes.feeDivider} />
            <div className={classes.feeRow}>
              <span className={classes.feeLabelBold}>Deine Auszahlung</span>
              <span className={classes.feeValueBold}>100,00 €</span>
            </div>
          </div>
        </div>

        {/* Action Links */}
        <div className={classes.actionsSection}>
          <button
            type="button"
            className={classes.actionCard}
            onClick={() => navigate('/finanzen/dashboard')}
          >
            <div className={classes.actionIconContainer}>
              <span className={classes.actionIcon}>📈</span>
            </div>
            <div className={classes.actionContent}>
              <div className={classes.actionTitle}>Finance Dashboard</div>
              <div className={classes.actionSubtitle}>Detaillierte Einnahmen-Analyse & Export</div>
            </div>
            <span className={classes.actionArrow}>→</span>
          </button>

          <button
            type="button"
            className={classes.actionCard}
            onClick={handleOpenStripeDashboard}
            disabled={linkLoading}
          >
            <div className={classes.actionIconContainer}>
              <span className={classes.actionIcon}>📊</span>
            </div>
            <div className={classes.actionContent}>
              <div className={classes.actionTitle}>Stripe Dashboard</div>
              <div className={classes.actionSubtitle}>Detaillierte Übersicht & Einstellungen</div>
            </div>
            {linkLoading ? (
              <div className={classes.loadingSpinner} />
            ) : (
              <span className={classes.actionArrow}>→</span>
            )}
          </button>

          <button
            type="button"
            className={classes.actionCard}
            onClick={handleOpenStripeDashboard}
            disabled={linkLoading}
          >
            <div className={classes.actionIconContainer}>
              <span className={classes.actionIcon}>🏦</span>
            </div>
            <div className={classes.actionContent}>
              <div className={classes.actionTitle}>Bankdaten ändern</div>
              <div className={classes.actionSubtitle}>IBAN & Kontoinhaber verwalten</div>
            </div>
            <span className={classes.actionArrow}>→</span>
          </button>

          <button
            type="button"
            className={classes.actionCard}
            onClick={handleExportTaxReport}
          >
            <div className={classes.actionIconContainer}>
              <span className={classes.actionIcon}>📄</span>
            </div>
            <div className={classes.actionContent}>
              <div className={classes.actionTitle}>Finanzamt-Export</div>
              <div className={classes.actionSubtitle}>Einnahmen-Übersicht teilen</div>
            </div>
            <span className={classes.actionArrow}>→</span>
          </button>
        </div>

        {/* Payout Status Summary */}
        <div className={classes.statusSummary}>
          <div className={classes.summaryTitle}>Status-Details</div>

          <div className={classes.summaryRow}>
            <span className={classes.summaryLabel}>Zahlungen empfangen</span>
            <div
              className={classes.summaryBadge}
              style={{
                backgroundColor: payoutInfo?.chargesEnabled ? 'var(--black)' : 'var(--dark-grey-3)',
              }}
            >
              <span className={classes.summaryBadgeText}>
                {payoutInfo?.chargesEnabled ? '✓ Aktiv' : '✗ Inaktiv'}
              </span>
            </div>
          </div>

          <div className={classes.summaryRow}>
            <span className={classes.summaryLabel}>Auszahlungen</span>
            <div
              className={classes.summaryBadge}
              style={{
                backgroundColor: payoutInfo?.payoutsEnabled ? 'var(--black)' : 'var(--dark-grey-3)',
              }}
            >
              <span className={classes.summaryBadgeText}>
                {payoutInfo?.payoutsEnabled ? '✓ Aktiv' : '✗ Inaktiv'}
              </span>
            </div>
          </div>
        </div>

        <div className={classes.bottomSpacer} />
      </div>
    </div>
  );
};

export default PayoutDashboard;
